// src/components/Landing/BookDemo.js
import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import './BookDemo.css';

// Componente do modal do Calendly (reutilizado da LandingPage)
const CalendlyModal = ({ onClose }) => {
  const modalRef = useRef(null);
  const calendlyRef = useRef(null);
  
  useEffect(() => {
    if (calendlyRef.current) {
      try {
        calendlyRef.current.innerHTML = `
          <iframe src="https://calendly.com/fabricio-venturi/new-meeting" 
                 frameborder="0" 
                 width="100%" 
                 height="100%"
                 title="Calendly"
                 allowtransparency="true"></iframe>
        `;
      } catch (error) {
        console.error("Erro ao injetar iframe do Calendly:", error);
      }
    }
    
    return () => {
      if (calendlyRef.current) {
        calendlyRef.current.innerHTML = '';
      }
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose]);

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    
    window.addEventListener('keydown', handleEscape);
    
    return () => {
      window.removeEventListener('keydown', handleEscape);
    };
  }, [onClose]);
  
  return (
    <div className="demo-calendly-modal">
      <div className="demo-calendly-modal-content" ref={modalRef}>
        <button 
          className="demo-calendly-close" 
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
        >
          ×
        </button>
        <div className="demo-calendly-iframe-container" ref={calendlyRef}>
          {/* O iframe será injetado aqui via useEffect */}
        </div>
      </div>
    </div>
  );
};

const BookDemo = () => {
  const [showCalendlyModal, setShowCalendlyModal] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (showCalendlyModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [showCalendlyModal]);

  const handleScheduleMeeting = (e) => {
    e.preventDefault();
    setTimeout(() => {
      setShowCalendlyModal(true);
    }, 50);
  };

  const closeCalendlyModal = () => {
    setShowCalendlyModal(false);
  };

  return (
    <div className="demo-page">
      {/* Header Navigation */}
      <nav className="demo-nav">
        <div className="demo-nav-content">
          <Link to="/" className="demo-logo">
            <img src="/logolandingpage.png" alt="DRE Pro Logo" />
          </Link>
          <Link to="/" className="demo-back-btn">
            <svg viewBox="0 0 24 24" width="20" height="20">
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
            <span>Voltar ao Site</span>
          </Link>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="demo-hero">
        <div className="demo-hero-background">
          <div className="demo-hero-shapes">
            <div className="demo-shape demo-shape-1"></div>
            <div className="demo-shape demo-shape-2"></div>
            <div className="demo-shape demo-shape-3"></div>
          </div>
        </div>
        <div className="demo-hero-content">
          <div className="demo-hero-badge">
            <span className="demo-badge-icon">🎯</span>
            <span>Demonstração Gratuita • 15 minutos</span>
          </div>
          <h1 className="demo-hero-title">
            Veja o <span className="demo-highlight">DRE Pro</span> em Ação
          </h1>
          <p className="demo-hero-subtitle">
            Descubra como transformar sua gestão financeira em apenas 15 minutos. 
            Nossa demonstração personalizada mostrará exatamente como o DRE Pro 
            pode revolucionar seu negócio.
          </p>
          
          <div className="demo-hero-stats">
            <div className="demo-stat">
              <div className="demo-stat-number">15min</div>
              <div className="demo-stat-label">Demonstração Rápida</div>
            </div>
            <div className="demo-stat">
              <div className="demo-stat-number">100%</div>
              <div className="demo-stat-label">Personalizada</div>
            </div>
            <div className="demo-stat">
              <div className="demo-stat-number">0%</div>
              <div className="demo-stat-label">Compromisso</div>
            </div>
          </div>

          <div className="demo-hero-cta">
            <button onClick={handleScheduleMeeting} className="demo-btn demo-btn-primary demo-pulse">
              <span className="demo-btn-icon">📅</span>
              <span>Agendar Demonstração Gratuita</span>
              <svg className="demo-btn-arrow" viewBox="0 0 24 24" width="20" height="20">
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </button>
            <p className="demo-cta-note">⚡ Disponível hoje • Sem compromisso • 100% gratuito</p>
          </div>
        </div>
      </section>

      {/* What You'll See Section */}
      <section className="demo-what-section">
        <div className="demo-container">
          <div className="demo-section-header">
            <h2>O Que Você Verá na Demonstração</h2>
            <p>Uma apresentação completa e personalizada das funcionalidades que importam para seu negócio</p>
          </div>

          <div className="demo-what-grid">
            <div className="demo-what-card">
              <div className="demo-card-number">1</div>
              <div className="demo-card-icon">📊</div>
              <h3>Dashboard em Tempo Real</h3>
              <p>Visualize como seus dados financeiros ficam organizados automaticamente em um painel intuitivo e interativo.</p>
            </div>

            <div className="demo-what-card">
              <div className="demo-card-number">2</div>
              <div className="demo-card-icon">🤖</div>
              <h3>IA em Funcionamento</h3>
              <p>Veja nossa inteligência artificial categorizando transações automaticamente e aprendendo com seus padrões.</p>
            </div>

            <div className="demo-what-card">
              <div className="demo-card-number">3</div>
              <div className="demo-card-icon">📈</div>
              <h3>Relatórios Instantâneos</h3>
              <p>Demonstração de como gerar relatórios DRE profissionais em segundos, não horas.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="demo-benefits-section">
        <div className="demo-container">
          <div className="demo-benefits-content">
            <div className="demo-benefits-text">
              <h2>Por Que Agendar Sua Demonstração?</h2>
              <div className="demo-benefits-list">
                <div className="demo-benefit-item">
                  <div className="demo-benefit-icon">✅</div>
                  <div className="demo-benefit-content">
                    <h4>Veja Resultados Reais</h4>
                    <p>Demonstração com dados reais similares ao seu negócio para você entender o impacto imediato.</p>
                  </div>
                </div>

                <div className="demo-benefit-item">
                  <div className="demo-benefit-icon">⚡</div>
                  <div className="demo-benefit-content">
                    <h4>Configuração Personalizada</h4>
                    <p>Nosso especialista mostrará como configurar o sistema especificamente para suas necessidades.</p>
                  </div>
                </div>

                <div className="demo-benefit-item">
                  <div className="demo-benefit-icon">🎯</div>
                  <div className="demo-benefit-content">
                    <h4>Plano de Implementação</h4>
                    <p>Saia da reunião com um plano claro de como implementar e maximizar seus resultados.</p>
                  </div>
                </div>

                <div className="demo-benefit-item">
                 
                </div>
              </div>
            </div>

            <div className="demo-benefits-cta">
              <div className="demo-cta-card">
                <h3>Pronto para Ver a Magia Acontecer?</h3>
                <p>Demonstração personalizada • 15 minutos • Sem compromisso</p>
                <button onClick={handleScheduleMeeting} className="demo-btn demo-btn-primary">
                  <span>Agendar Agora</span>
                </button>
                <div className="demo-urgency">
                  <span className="demo-urgency-icon">⏰</span>
                  <span>Vagas limitadas disponíveis esta semana</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="demo-process-section">
        <div className="demo-container">
          <div className="demo-section-header">
            <h2>Como Funciona Nossa Demonstração</h2>
            <p>Um processo simples e direto, focado em seus resultados</p>
          </div>

          <div className="demo-process-timeline">
            <div className="demo-timeline-item">
              <div className="demo-timeline-icon">📞</div>
              <div className="demo-timeline-content">
                <h3>1. Conversa Inicial</h3>
                <p>Entendemos seu negócio e principais desafios financeiros (3 minutos)</p>
              </div>
            </div>

            <div className="demo-timeline-item">
              <div className="demo-timeline-icon">💻</div>
              <div className="demo-timeline-content">
                <h3>2. Demonstração Ao Vivo</h3>
                <p>Mostramos o sistema funcionando com cenários do seu setor (10 minutos)</p>
              </div>
            </div>

            <div className="demo-timeline-item">
              <div className="demo-timeline-icon">🎯</div>
              <div className="demo-timeline-content">
                <h3>3. Próximos Passos</h3>
                <p>Definimos juntos o melhor plano e processo de implementação (2 minutos)</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Social Proof Section */}
      <section className="demo-social-section">
        <div className="demo-container">
          <div className="demo-section-header">
            <h2>Empresários Que Já Viram a Demonstração</h2>
            <p>Veja o que outros empresários falaram sobre nossa demonstração</p>
          </div>

          <div className="demo-testimonials">
            <div className="demo-testimonial">
              <div className="demo-testimonial-content">
                <p>"A demonstração foi incrível! Em 15 minutos entendi como economizar 20+ horas por mês na minha gestão financeira."</p>
              </div>
              <div className="demo-testimonial-author">
                <div className="demo-author-avatar">JM</div>
                <div className="demo-author-info">
                  <h4>João Mendes</h4>
                  <p>CEO, TechSolutions</p>
                </div>
              </div>
            </div>

            <div className="demo-testimonial">
              <div className="demo-testimonial-content">
                <p>"Saí da demonstração já sabendo que era exatamente o que minha empresa precisava. Implementamos no mesmo dia!"</p>
              </div>
              <div className="demo-testimonial-author">
                <div className="demo-author-avatar">MA</div>
                <div className="demo-author-info">
                  <h4>Maria Almeida</h4>
                  <p>Diretora, Construviva</p>
                </div>
              </div>
            </div>

            <div className="demo-testimonial">
              <div className="demo-testimonial-content">
                <p>"A equipe mostrou exatamente como o sistema se aplicaria ao meu restaurante. Muito profissional e didático."</p>
              </div>
              <div className="demo-testimonial-author">
                <div className="demo-author-avatar">GP</div>
                <div className="demo-author-info">
                  <h4>Gabriel Pereira</h4>
                  <p>Proprietário, Sabor & Arte</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="demo-faq-section">
        <div className="demo-container">
          <div className="demo-section-header">
            <h2>Perguntas Frequentes</h2>
            <p>Esclarecemos suas dúvidas sobre a demonstração</p>
          </div>

          <div className="demo-faq-grid">
            <div className="demo-faq-item">
              <h3>A demonstração é realmente gratuita?</h3>
              <p>Sim! Completamente gratuita e sem qualquer compromisso. Queremos que você conheça nossa solução antes de tomar qualquer decisão.</p>
            </div>

            <div className="demo-faq-item">
              <h3>Quanto tempo dura a demonstração?</h3>
              <p>Aproximadamente 15 minutos. Focamos nos pontos mais importantes e relevantes para seu negócio, sem perder seu tempo.</p>
            </div>

            <div className="demo-faq-item">
              <h3>Preciso preparar alguma coisa?</h3>
              <p>Não é necessário! Apenas tenha em mente seus principais desafios de gestão financeira para personalizarmos a demonstração.</p>
            </div>

            <div className="demo-faq-item">
              <h3>A demonstração é por vídeo chamada?</h3>
              <p>Sim, utilizamos Google Meet ou Zoom para uma experiência interativa onde você pode tirar dúvidas em tempo real.</p>
            </div>

            <div className="demo-faq-item">
              <h3>E se eu não conseguir participar no horário agendado?</h3>
              <p>Sem problemas! Você pode reagendar facilmente através do link que enviamos ou entrar em contato conosco.</p>
            </div>

            <div className="demo-faq-item">
              <h3>Vou receber muitas ligações de vendas depois?</h3>
              <p>Não fazemos vendas agressivas. Após a demonstração, enviamos apenas um e-mail com um resumo e ficamos disponíveis se precisar de ajuda.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="demo-final-cta">
        <div className="demo-cta-background">
          <div className="demo-cta-shapes">
            <div className="demo-cta-shape-1"></div>
            <div className="demo-cta-shape-2"></div>
          </div>
        </div>
        <div className="demo-container">
          <div className="demo-final-content">
            <h2>Não Perca Esta Oportunidade</h2>
            <p>
              Milhares de empresários já descobriram como economizar tempo e aumentar 
              a eficiência com o DRE Pro. Seja o próximo a transformar sua gestão financeira.
            </p>
            
            <div className="demo-final-features">
              <div className="demo-final-feature">
                <span className="demo-feature-icon">⚡</span>
                <span>Implementação Rápida</span>
              </div>
              <div className="demo-final-feature">
                <span className="demo-feature-icon">🎯</span>
                <span>Resultados Imediatos</span>
              </div>
              <div className="demo-final-feature">
                <span className="demo-feature-icon">🤝</span>
                <span>Suporte Especializado</span>
              </div>
            </div>

            <button onClick={handleScheduleMeeting} className="demo-btn demo-btn-final">
              <span className="demo-btn-icon">🚀</span>
              <span>Agendar Minha Demonstração Gratuita</span>
              <svg className="demo-btn-arrow" viewBox="0 0 24 24" width="20" height="20">
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </button>

            <div className="demo-guarantee">
              <span className="demo-guarantee-icon">🛡️</span>
              <span>Garantia: 15 minutos ou menos • Sem compromisso • 100% gratuito</span>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="demo-footer">
        <div className="demo-footer-content">
          <Link to="/" className="demo-footer-logo">
            <img src="/logolandingpage.png" alt="DRE Pro Logo" />
          </Link>
          <p>&copy; {new Date().getFullYear()} DRE Pro. Transformando gestão financeira desde 2024.</p>
          <Link to="/" className="demo-footer-back">
            Voltar ao Site Principal
          </Link>
        </div>
      </footer>

      {/* Calendly Modal */}
      {showCalendlyModal && <CalendlyModal onClose={closeCalendlyModal} />}
    </div>
  );
};

export default BookDemo;